import { store } from '@/app/store';
import { selectAllPortfolioBalances, useGetPortfolioBalancesQuery } from '@/features/balance/balancesApi';
import { useGetMarketInfoQuery } from '@/features/marketInfo/marketInfoApi';
import { selectUser, useGetUserQuery } from '@/features/user/userApi';
import { calculateEuroValue } from '@/shared/helpers/portfolioHelpers';
import { IonCol } from '@ionic/react';
import { head, orderBy } from 'lodash';
import { useMemo } from 'react';
import { TravelRuleType, withdrawCryptoState } from '@/types/crypto';
import { selectIsLoggedIn } from '@/features/oAuth/oAuthSlice';
import AssetList from '@/shared/components/AssetList/AssetList';
import PortfolioValue from '@/shared/components/PortfolioValue/PortfolioValue';
import { useMarketInfo } from '@/shared/hooks/market';
import { userCompletedKyc } from '@/shared/hooks/user';
import { useSelector } from 'react-redux';
import { ASSETS_NAMES } from '@/shared/utils/constants';
import { useSelectedAssetId } from '@/shared/hooks/assets';

type Props = {
  handleInitTravelRule: (value: boolean, withdrawCryptoData: withdrawCryptoState, type?: TravelRuleType) => void;
};

const WalletList: React.FC<Props> = ({ handleInitTravelRule }) => {
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const { isSuccess: marketInfoSuccess } = useGetMarketInfoQuery(undefined);

  const { isSuccess: portfolioBalancesSuccess } = useGetPortfolioBalancesQuery(undefined, {
    skip: !isLoggedIn
  });

  const { isSuccess: userSuccess } = useGetUserQuery(undefined, {
    skip: !isLoggedIn
  });

  const { marketsInfo } = useMarketInfo();

  const assetsPortfolioBalances = selectAllPortfolioBalances(store.getState());

  const user = selectUser(store.getState());

  const walletBalances = useMemo(
    () => calculateEuroValue(assetsPortfolioBalances, marketsInfo),
    [assetsPortfolioBalances, marketsInfo]
  );

  const sortedWalletBalances = useMemo(
    () =>
      orderBy(
        walletBalances,
        [(asset) => Number(asset.euroValue), (asset) => ASSETS_NAMES[asset.assetId]],
        ['desc', 'asc']
      ),
    [walletBalances]
  );

  const selectedAssetId = useSelectedAssetId();

  const currentUser = head(user);
  const userIsKyced = currentUser ? userCompletedKyc(currentUser) : false;

  return (
    <IonCol sizeLg="5" sizeMd="12">
      <PortfolioValue />
      <AssetList
        selectedAsset={selectedAssetId}
        assetsPortfolioBalances={
          marketInfoSuccess && portfolioBalancesSuccess && userSuccess && userIsKyced ? sortedWalletBalances : []
        }
        handleInitTravelRule={handleInitTravelRule}
      />
    </IonCol>
  );
};

export default WalletList;
